import { WizardStep } from '@app/app/_components/Wizard/Wizard';
import { HOME_INSURANCE_STEPS } from '@app/app/esales/home/_sections/steps/HOME_INSURANCE_STEPS';
import { ObjectSummaryEditableStep } from '@app/app/esales/home/_sections/steps/ObjectSummaryStep/ObjectSummaryEditableStep';
import { ObjectSummaryReadOnlyStep } from '@app/app/esales/home/_sections/steps/ObjectSummaryStep/ObjectSummaryReadOnlyStep';
import { ProductDataObject } from '@app/domain/model/eshop/home/ProductDataObject';
import { DiscountCodeData } from '@app/domain/model/eshop/transport/DiscountCodeData';
import { ProductDataLeasing } from '@app/domain/model/eshop/transport/ProductDataLeasing';
import { ProductDataPurpose } from '@app/domain/model/eshop/transport/ProductDataPurpose';
import { FormState } from '@app/lib/form/contracts/FormState';
import { PurchaseHomeInsuranceResult } from '@app/service/eshop/home/actions/purchaseHomeInsurance';
import { HomeInsuranceRequestResult } from '@app/service/eshop/home/actions/requestHomeInsurance';
import { InsuranceRisksStep } from '@app/app/esales/home/_sections/steps/InsuranceRisksStep/InsuranceRisksStep';
import { HomeInsuranceRiskPackage } from '@app/domain/model/eshop/home/HomeInsuranceRiskPackage';
import { InsuranceStartDateStep } from '@app/app/esales/home/_sections/steps/InsuranceStartDateStep';
import { ProductDataRider } from '@app/domain/model/eshop/transport/ProductDataRider';
import { AdditionalRisksStep } from '@app/app/esales/home/_sections/steps/AdditionalRisksStep/AdditionalRisksStep';
import { PolicyholderInformationStep } from '@app/app/esales/home/_sections/steps/PolicyholderInformationStep/PolicyholderInformationStep';
import {
    PolicyholderFormFields,
    PolicyholderFormHandlers,
} from '@app/app/esales/home/_sections/HomeInsuranceWizardSection/hooks/usePolicyholderFormFields';
import { Card } from '@app/app/_components/Card/Card';
import { Tenant } from '@app/infrastructure/tenant/model/Tenant';
import { SummaryStep } from '@app/app/esales/home/_sections/steps/SummaryStep/SummaryStep';
import { InformationPopover } from '@app/app/_components/InformationPopover/InformationPopover';
import { QuestionCircleIcon } from '@app/app/_icons/QuestionCircleIcon';
import { PaymentMethodType } from '@app/domain/model/PaymentMethodType';
import { PaymentMethodStep } from '@app/app/esales/_sections/steps/PaymentMethodStep';
import { PaymentOptionsStep } from '@app/app/esales/home/_sections/steps/PaymentOptionsStep/PaymentOptionsStep';
import { PaymentMethodTypeStep } from '@app/app/esales/home/_sections/steps/PaymentMethodTypeStep/PaymentMethodTypeStep';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { HomeInsurancePremiumResponse } from '@app/domain/model/eshop/home/HomeInsurancePremiumResponse';
import { CustomerLoginStep } from '@app/app/esales/_sections/steps/CustomerLoginStep';
import { AuthState } from '@app/infrastructure/auth/model/AuthState';
import { Client } from '@app/domain/model/Client';
import { PlatformSettings } from '@app/domain/model/PlatformSettings';

interface Props {
    t: (key: string) => string;
    requestResult: Nullable<HomeInsuranceRequestResult>;
    handleDiscountCode: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleApplyDiscountClick: () => Promise<void>;
    discountCodeData: DiscountCodeData;
    leasings: ProductDataLeasing[];
    formState: FormState<PurchaseHomeInsuranceResult>;
    objects: ProductDataObject[];
    purposes: Nullable<ProductDataPurpose>;
    selectedRiskCode: string;
    handleRisksChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    selectedRisk: Nullable<HomeInsuranceRiskPackage>;
    riders: ProductDataRider[];
    selectedRiders: ProductDataRider[];
    handleRidersChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    policyholderFields: PolicyholderFormFields;
    policyholderHandlers: PolicyholderFormHandlers;
    tenant: Tenant;
    onSavePolicyholder: (data: PolicyholderFormFields) => void;
    totalPrice: number;
    priceToPayNow: number;
    paymentFrequency: number;
    onPaymentFrequencyChange: (paymentFrequency: number) => void;
    editObjectSummary: () => void;
    editPolicy: () => void;
    editBeginDate: () => void;
    editAdditionalRisks: () => void;
    handlePledgedToBankChange: (value: string) => void;
    pledgedToBank: Nullable<string>;
    selectedLeasing: Nullable<string>;
    handleSelectedLeasingChange: (value: string) => void;
    handlePaymentMethodTypeChange: (paymentMethodType: Nullable<string>) => void;
    selectedPaymentMethodType: Nullable<string>;
    currentPremiums: Nullable<HomeInsurancePremiumResponse>;
    authState: AuthState;
    clients: Client[];
    swedbankLogoSrc: string;
    sebLogoSrc: string;
    onSuccessfulLogin: () => void;
    settings: PlatformSettings;
    initialBeginDate?: string;
}

export function buildSteps({
    t,
    requestResult,
    handleDiscountCode,
    handleApplyDiscountClick,
    discountCodeData,
    leasings,
    formState,
    objects,
    purposes,
    selectedRiskCode,
    handleRisksChange,
    riders,
    selectedRiders,
    handleRidersChange,
    policyholderFields,
    policyholderHandlers,
    tenant,
    onSavePolicyholder,
    selectedRisk,
    totalPrice,
    priceToPayNow,
    paymentFrequency,
    onPaymentFrequencyChange,
    editObjectSummary,
    editPolicy,
    editBeginDate,
    editAdditionalRisks,
    handlePledgedToBankChange,
    pledgedToBank,
    selectedLeasing,
    handleSelectedLeasingChange,
    handlePaymentMethodTypeChange,
    selectedPaymentMethodType,
    currentPremiums,
    authState,
    clients,
    swedbankLogoSrc,
    sebLogoSrc,
    onSuccessfulLogin,
    settings,
    initialBeginDate,
}: Props): WizardStep[] {
    return [
        {
            label: t('eshop.home.propertyInsuranceTitle'),
            subSteps: [
                {
                    key: HOME_INSURANCE_STEPS.OBJECT_SUMMARY_READ_ONLY,
                    when: () =>
                        requestResult?.propertyInfo != null && requestResult.acceptedToRecieveInfoFromRegisterCenter,
                    title: t('eshop.home.objectSummaryTitle'),
                    body: (
                        <ObjectSummaryReadOnlyStep
                            handlePledgedToBankChange={handlePledgedToBankChange}
                            pledgedToBank={pledgedToBank}
                            selectedLeasing={selectedLeasing}
                            handleSelectedLeasingChange={handleSelectedLeasingChange}
                            propertyInfo={requestResult?.propertyInfo ?? null}
                            discountCodeData={discountCodeData}
                            address={requestResult?.address ?? null}
                            handleApplyDiscountClick={handleApplyDiscountClick}
                            handleDiscountCode={handleDiscountCode}
                            leasings={leasings}
                            formState={formState}
                            objects={objects}
                            purposes={purposes}
                        />
                    ),
                },
                {
                    key: HOME_INSURANCE_STEPS.OBJECT_SUMMARY_EDITABLE,
                    when: () =>
                        requestResult?.propertyInfo == null || !requestResult.acceptedToRecieveInfoFromRegisterCenter,
                    title: t('eshop.home.objectSummaryTitle'),
                    body: (
                        <ObjectSummaryEditableStep
                            objects={objects}
                            formState={formState}
                            personalCode={requestResult?.policyHolderCode ?? ''}
                            address={requestResult?.address ?? null}
                            typeOfConstruction={purposes}
                            discountCodeData={discountCodeData}
                            handleApplyDiscountClick={handleApplyDiscountClick}
                            handleDiscountCode={handleDiscountCode}
                            leasings={leasings}
                            handlePledgedToBankChange={handlePledgedToBankChange}
                            pledgedToBank={pledgedToBank}
                            selectedLeasing={selectedLeasing}
                            handleSelectedLeasingChange={handleSelectedLeasingChange}
                            defaultObjectType={formState.data?.formData?.objectType}
                            defaultConstructionType={formState.data?.formData?.constructionType}
                            defaultTotalArea={formState.data?.formData?.totalArea?.toString()}
                            defaultConstructionYear={formState.data?.formData?.constructionYear?.toString()}
                        />
                    ),
                },
                {
                    key: HOME_INSURANCE_STEPS.CHOOSE_INSURANCE,
                    title: (
                        <div className="flex w-full flex-row items-center justify-between gap-4">
                            {t('eshop.home.chooseInsurance')}
                            <InformationPopover
                                className="absolute right-0"
                                content={<Paragraph>{t('eshop.home.chooseInsuranceInfo')}</Paragraph>}>
                                <QuestionCircleIcon className="size-6 text-neutral-500" />
                            </InformationPopover>
                        </div>
                    ),
                    body: (
                        <InsuranceRisksStep
                            onChange={handleRisksChange}
                            risks={currentPremiums?.riskPackages ?? []}
                            value={selectedRiskCode}
                        />
                    ),
                },
                {
                    key: HOME_INSURANCE_STEPS.INSURANCE_BEGIN_DATE,
                    title: t('eshop.home.insuranceStartDate'),
                    body: <InsuranceStartDateStep initialBeginDate={initialBeginDate} />,
                },
                {
                    key: HOME_INSURANCE_STEPS.ADDITIONAL_RISKS,
                    when: () => riders.length > 0,
                    title: t('eshop.home.additionalRisks'),
                    body: (
                        <AdditionalRisksStep
                            handleRidersChange={handleRidersChange}
                            selectedRiders={selectedRiders}
                            riders={riders}
                        />
                    ),
                },
                {
                    key: HOME_INSURANCE_STEPS.CUSTOMER_LOGIN,
                    when: () =>
                        settings.general.esalesHomeAuthEnabled &&
                        (!authState.authenticated || !authState.clientSelected),
                    title: t('eshop.general.ifYouAlreadyClient'),
                    body: (
                        <CustomerLoginStep
                            swedbankLogoSrc={swedbankLogoSrc}
                            sebLogoSrc={sebLogoSrc}
                            clients={clients}
                            authState={authState}
                            onContinue={onSuccessfulLogin}
                            onSuccess={onSuccessfulLogin}
                            bankAuthEnabled={settings.general.esalesHomeBankAuthEnabled}
                        />
                    ),
                },
                {
                    key: HOME_INSURANCE_STEPS.POLICYHOLDER_INFORMATION,
                    title: t('eshop.home.policyholderInformationTitle'),
                    body: (
                        <Card className="flex flex-col gap-8 p-6">
                            <PolicyholderInformationStep
                                key={policyholderFields.address ? 'filled' : 'empty'}
                                formState={formState}
                                tenant={tenant}
                                personalCode={requestResult?.policyHolderCode ?? ''}
                                policyholderFields={policyholderFields}
                                policyholderHandlers={policyholderHandlers}
                            />
                        </Card>
                    ),
                },
                {
                    key: HOME_INSURANCE_STEPS.SUMMARY,
                    title: t('eshop.home.summary.summaryStepTitle'),
                    body: (
                        <SummaryStep
                            formState={formState}
                            requestResult={requestResult}
                            policyholderFields={policyholderFields}
                            personalCode={requestResult?.policyHolderCode ?? ''}
                            tenant={tenant}
                            onSavePolicyholder={onSavePolicyholder}
                            objects={objects}
                            purposes={purposes}
                            leasings={leasings}
                            selectedRisk={selectedRisk}
                            selectedRiders={selectedRiders}
                            totalPrice={totalPrice}
                            priceToPayNow={priceToPayNow}
                            paymentFrequency={paymentFrequency}
                            onPaymentFrequencyChange={onPaymentFrequencyChange}
                            editObjectSummary={editObjectSummary}
                            editPolicy={editPolicy}
                            editBeginDate={editBeginDate}
                            editAdditionalRisks={editAdditionalRisks}
                            handlePledgedToBankChange={handlePledgedToBankChange}
                            pledgedToBank={pledgedToBank}
                            selectedLeasing={selectedLeasing}
                            handleSelectedLeasingChange={handleSelectedLeasingChange}
                        />
                    ),
                },
            ],
        },
        {
            label: t('eshop.home.paymentStepTitle'),
            subSteps: [
                {
                    key: HOME_INSURANCE_STEPS.PAYMENT.OPTIONS,
                    title: (
                        <div className="flex w-full flex-row items-center justify-between gap-4">
                            {t('eshop.home.choosePaymentOptions')}
                            <InformationPopover
                                className="absolute right-0"
                                content={t('eshop.home.paymentOptionsTooltip')}>
                                <QuestionCircleIcon className="size-6 text-neutral-500" />
                            </InformationPopover>
                        </div>
                    ),
                    body: (
                        <PaymentOptionsStep
                            formState={formState}
                            paymentFrequency={paymentFrequency}
                            selectedRiders={selectedRiders}
                        />
                    ),
                },
                {
                    key: HOME_INSURANCE_STEPS.PAYMENT.METHOD_TYPE,
                    title: t('eshop.general.choosePaymentMethod'),
                    body: <PaymentMethodTypeStep formState={formState} onChange={handlePaymentMethodTypeChange} />,
                },
                {
                    key: HOME_INSURANCE_STEPS.PAYMENT.METHOD,
                    when: () => selectedPaymentMethodType === PaymentMethodType.Banks,
                    title: t('eshop.general.choosePaymentMethod'),
                    body: <PaymentMethodStep paymentMethods={formState.data?.paymentMethods ?? []} />,
                },
            ],
        },
    ];
}
